// src/components/ui/Layout/Container.tsx
// Meso layout: Max-width constraint container following IonicLayout.md

import React, { forwardRef } from 'react';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type ContainerPadding = 'none' | 'sm' | 'md' | 'lg';

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Max-width constraint (default: 'md') */
  size?: ContainerSize;
  /** Horizontal padding (default: 'md') */
  padding?: ContainerPadding;
  /** Semantic HTML element */
  as?: 'div' | 'section' | 'article' | 'main' | 'aside';
  /** Center the container horizontally */
  centered?: boolean;
  children: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const sizeMap: Record<ContainerSize, string> = {
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1440px',
  full: '100%',
};

const paddingMap: Record<ContainerPadding, string> = {
  none: '0',
  sm: '8px',
  md: '16px',
  lg: '24px',
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      size = 'md',
      padding = 'md',
      as: Element = 'div',
      centered = true,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const computedStyle: React.CSSProperties = {
      maxWidth: sizeMap[size],
      width: '100%',
      paddingLeft: paddingMap[padding],
      paddingRight: paddingMap[padding],
      ...(centered && {
        marginLeft: 'auto',
        marginRight: 'auto',
      }),
      ...style,
    };

    return (
      <Element
        ref={ref}
        style={computedStyle}
        data-layout="Container"
        data-size={size}
        {...rest}
      >
        {children}
      </Element>
    );
  }
);

Container.displayName = 'Container';

export default Container;
